// flash.ts — opt-in flash lighting capture (ARCHITECTURE.md §16.1). The
// FlashController owns the camera track's torch and exposure controls for one
// capture: it reads the track's capabilities, toggles the torch on a fixed
// 50% duty cycle (each state held FLASH_PERIOD_MS, so FLASH_SETTLE_MS still
// leaves usable frames in every phase), keeps the log of APPLIED toggles
// (the commanded state, timestamped when applyConstraints resolves), answers
// "was the torch on at time t?" for keyframe tagging (stateAt; 'unknown' near
// a toggle or while one is in flight), locks exposure / white balance once a
// marker is seen when the browser supports manual modes, and samples the
// track's exposure settings about once a second as a logged time series.
// stop() always commands the torch off — the torch must never be left on.
// Capabilities missing at start are re-read for CAPS_WAIT_MS (Chrome on
// Android reports torch late); `ready` resolves with the final answer.
//
// FlashLogUploader batches the toggle log to POST /api/scans/{id}/flash.

import type { ExposureSample, FlashEvent, FlashState } from '../api';

export const FLASH_PERIOD_MS = 500;
export const FLASH_SETTLE_MS = 300;
export const EXPOSURE_SAMPLE_MS = 1000;
export const FLASH_UPLOAD_EVERY_MS = 2000;
// Chrome on Android fills in getCapabilities() (torch, exposure modes) a
// moment after the stream starts, so missing caps are re-read every
// CAPS_RETRY_MS for up to CAPS_WAIT_MS before the torch counts as unsupported.
export const CAPS_RETRY_MS = 250;
export const CAPS_WAIT_MS = 3000;

// FlashTrackLike is the slice of MediaStreamTrack the controller uses.
export interface FlashTrackLike {
  getCapabilities?: () => object;
  getSettings?: () => object;
  applyConstraints: (c: MediaTrackConstraints) => Promise<void>;
}

// FlashClock is the time source and timer (faked in tests).
export interface FlashClock {
  now(): number;
  every(fn: () => void, ms: number): unknown;
  cancel(handle: unknown): void;
}

export const browserClock: FlashClock = {
  now: () => performance.now(),
  every: (fn, ms) => globalThis.setInterval(fn, ms),
  cancel: (h) => globalThis.clearInterval(h as ReturnType<typeof setInterval>),
};

export interface FlashCaps {
  torch: boolean;
  exposureManual: boolean;
  whiteBalanceManual: boolean;
}

export interface FlashTag {
  flash: FlashState;
  flash_changed_ms: number;
}

// readCaps interprets getCapabilities() (missing API → nothing supported).
export function readCaps(track: FlashTrackLike | null): FlashCaps {
  let c: Record<string, unknown> = {};
  try {
    c = (track?.getCapabilities?.() ?? {}) as Record<string, unknown>;
  } catch (err) {
    console.warn('[openscan/flash] getCapabilities threw', err);
  }
  const modes = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
  return {
    torch: c.torch === true || (Array.isArray(c.torch) && c.torch.includes(true)),
    exposureManual: modes(c.exposureMode).includes('manual'),
    whiteBalanceManual: modes(c.whiteBalanceMode).includes('manual'),
  };
}

// toExposureSample maps getSettings() to the snake_case upload shape.
export function toExposureSample(s: Record<string, unknown>, locked: boolean): ExposureSample {
  const out: ExposureSample = { locked };
  const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
  const em = str(s.exposureMode), et = num(s.exposureTime), iso = num(s.iso);
  const ec = num(s.exposureCompensation), ct = num(s.colorTemperature), wb = str(s.whiteBalanceMode);
  if (em !== undefined) out.exposure_mode = em;
  if (et !== undefined) out.exposure_time = et;
  if (iso !== undefined) out.iso = iso;
  if (ec !== undefined) out.exposure_compensation = ec;
  if (ct !== undefined) out.color_temperature = ct;
  if (wb !== undefined) out.white_balance_mode = wb;
  return out;
}

// applied is one resolved toggle: cmdT when it was commanded, t when applied.
interface Applied {
  cmdT: number;
  t: number;
  on: boolean;
}

export class FlashController {
  private capsNow: FlashCaps;
  private capsTimer: unknown = null;
  private capsSince = 0;
  private resolveReady: ((c: FlashCaps) => void) | null = null;
  // ready resolves with the final capabilities: at once when the torch is
  // there from the start, else when it appears or CAPS_WAIT_MS runs out.
  readonly ready: Promise<FlashCaps>;
  private applied: Applied[] = [];
  private fuzzy: Array<[number, number]> = []; // failed toggles: state uncertain in [a, b]
  private inflightSince: number | null = null;
  private pending: FlashEvent[] = [];
  private toggleTimer: unknown = null;
  private sampleTimer: unknown = null;
  private lastSample: ExposureSample | null = null;
  private lockState: 'none' | 'pending' | 'locked' | 'failed' = 'none';
  private stopped = false;
  private stopping: Promise<void> | null = null;
  private torchOn = false;

  constructor(private track: FlashTrackLike | null, private clock: FlashClock = browserClock) {
    this.capsNow = readCaps(track);
    this.ready = new Promise((r) => (this.resolveReady = r));
    console.info('[openscan/flash] capabilities', this.capsNow, track?.getCapabilities ? '' : '(no getCapabilities)');
    if (!this.capsNow.exposureManual) console.info('[openscan/flash] manual exposure not reported (yet): exposure stays automatic unless it appears');
  }

  get caps(): FlashCaps {
    return { ...this.capsNow };
  }

  private settleCaps(): void {
    if (this.capsTimer !== null) this.clock.cancel(this.capsTimer);
    this.capsTimer = null;
    if (!this.resolveReady) return;
    if (!this.capsNow.torch) console.info('[openscan/flash] torch unsupported on this camera/browser: keyframes are not flash-tagged');
    this.resolveReady?.(this.caps);
    this.resolveReady = null;
  }

  // recheckCaps re-reads late capabilities; the torch starts once it shows.
  private recheckCaps(): void {
    const c = readCaps(this.track);
    this.capsNow = {
      torch: this.capsNow.torch || c.torch,
      exposureManual: this.capsNow.exposureManual || c.exposureManual,
      whiteBalanceManual: this.capsNow.whiteBalanceManual || c.whiteBalanceManual,
    };
    if (this.capsNow.torch) {
      console.info(`[openscan/flash] torch reported after ${Math.round(this.clock.now() - this.capsSince)} ms`);
      this.startTorch();
      return this.settleCaps();
    }
    if (this.clock.now() - this.capsSince >= CAPS_WAIT_MS) this.settleCaps();
  }

  get torchSupported(): boolean {
    return this.capsNow.torch;
  }

  get exposureLocked(): boolean {
    return this.lockState === 'locked';
  }

  // start begins the torch duty cycle (when supported) and exposure sampling.
  start(): void {
    if (this.stopped || this.sampleTimer !== null) return;
    this.sample();
    this.sampleTimer = this.clock.every(() => this.sample(), EXPOSURE_SAMPLE_MS);
    if (this.capsNow.torch) {
      this.startTorch();
      return this.settleCaps();
    }
    if (!this.track?.getCapabilities) return this.settleCaps();
    this.capsSince = this.clock.now();
    this.capsTimer = this.clock.every(() => this.recheckCaps(), CAPS_RETRY_MS);
  }

  // startTorch begins the 50% duty cycle.
  private startTorch(): void {
    if (this.stopped || this.toggleTimer !== null) return;
    console.info(`[openscan/flash] torch toggles every ${FLASH_PERIOD_MS} ms (50% duty), settle ${FLASH_SETTLE_MS} ms`);
    void this.setTorch(true);
    this.toggleTimer = this.clock.every(() => {
      if (this.inflightSince === null) void this.setTorch(!this.torchOn);
      else console.debug('[openscan/flash] toggle skipped: previous one still in flight');
    }, FLASH_PERIOD_MS);
  }

  // setTorch commands the torch and records the state once it is applied.
  private async setTorch(on: boolean): Promise<void> {
    const cmdT = this.clock.now();
    this.inflightSince = cmdT;
    try {
      await this.track!.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] });
      const t = this.clock.now();
      this.torchOn = on;
      this.applied.push({ cmdT, t, on });
      this.pending.push({ t, on });
      if (t - cmdT > 150) console.debug(`[openscan/flash] torch ${on ? 'on' : 'off'} took ${Math.round(t - cmdT)} ms`);
    } catch (err) {
      const t = this.clock.now();
      this.fuzzy.push([cmdT, t]);
      console.warn(`[openscan/flash] torch ${on ? 'on' : 'off'} failed after ${Math.round(t - cmdT)} ms`, err);
    } finally {
      this.inflightSince = null;
    }
  }

  // stateAt is the torch state at tMs (performance.now() clock).
  stateAt(tMs: number): FlashTag {
    let idx = -1;
    for (let i = this.applied.length - 1; i >= 0; i--) {
      if (this.applied[i].t <= tMs) {
        idx = i;
        break;
      }
    }
    if (idx < 0) return { flash: 'unknown', flash_changed_ms: 0 };
    const e = this.applied[idx];
    const since = tMs - e.t;
    const tag = (flash: FlashState): FlashTag => ({ flash, flash_changed_ms: Math.round(since) });
    if (since < FLASH_SETTLE_MS) return tag('unknown');
    const next = this.applied[idx + 1];
    if (next && next.cmdT <= tMs) return tag('unknown'); // a toggle was in flight at tMs
    if (this.inflightSince !== null && this.inflightSince <= tMs) return tag('unknown');
    if (this.fuzzy.some(([a, b]) => tMs >= a && tMs <= b + FLASH_SETTLE_MS)) return tag('unknown');
    return tag(e.on ? 'on' : 'off');
  }

  // lockExposure locks exposure (and white balance) once, when supported.
  async lockExposure(): Promise<void> {
    if (this.stopped || this.lockState !== 'none' || !this.capsNow.exposureManual || !this.track) return;
    this.lockState = 'pending';
    const set: Record<string, string> = { exposureMode: 'manual' };
    if (this.capsNow.whiteBalanceManual) set.whiteBalanceMode = 'manual';
    try {
      await this.track.applyConstraints({ advanced: [set as MediaTrackConstraintSet] });
      this.lockState = 'locked';
      console.info('[openscan/flash] exposure locked', set);
      this.sample();
    } catch (err) {
      this.lockState = 'failed';
      console.warn('[openscan/flash] exposure lock failed; exposure stays automatic', err);
    }
  }

  // sample reads and logs the track's current exposure settings.
  private sample(): void {
    let s: Record<string, unknown> = {};
    try {
      s = (this.track?.getSettings?.() ?? {}) as Record<string, unknown>;
    } catch (err) {
      console.warn('[openscan/flash] getSettings threw', err);
      return;
    }
    this.lastSample = toExposureSample(s, this.exposureLocked);
    console.info(`[openscan/flash] exposure t=${Math.round(this.clock.now())}`, this.lastSample);
  }

  // latestExposure is the most recent settings sample (null before start).
  latestExposure(): ExposureSample | null {
    return this.lastSample ? { ...this.lastSample } : null;
  }

  // drainEvents returns (and forgets) the applied toggles not yet uploaded.
  drainEvents(): FlashEvent[] {
    const out = this.pending;
    this.pending = [];
    return out;
  }

  // requeue puts back events whose upload failed (in front of newer ones).
  requeue(events: FlashEvent[]): void {
    this.pending = [...events, ...this.pending];
  }

  // stop ends the duty cycle and commands the torch off. Idempotent: later
  // calls wait for the first one (so the final 'off' is in the log after).
  stop(): Promise<void> {
    this.stopping ??= this.doStop();
    return this.stopping;
  }

  private async doStop(): Promise<void> {
    this.stopped = true;
    if (this.toggleTimer !== null) this.clock.cancel(this.toggleTimer);
    if (this.sampleTimer !== null) this.clock.cancel(this.sampleTimer);
    this.toggleTimer = this.sampleTimer = null;
    this.settleCaps();
    if (!this.capsNow.torch || !this.track) return;
    try {
      await this.track.applyConstraints({ advanced: [{ torch: false } as MediaTrackConstraintSet] });
      const t = this.clock.now();
      if (this.torchOn) {
        this.applied.push({ cmdT: t, t, on: false });
        this.pending.push({ t, on: false });
      }
      this.torchOn = false;
      console.info(`[openscan/flash] stopped; torch off (${this.applied.length} toggles applied)`);
    } catch (err) {
      console.warn('[openscan/flash] torch off failed (stopping the track turns it off)', err);
    }
  }
}

// FlashLogApi is the slice of the REST client the uploader uses.
export interface FlashLogApi {
  postFlash(id: string, events: FlashEvent[]): Promise<void>;
}

// FlashLogUploader posts the controller's toggle log every
// FLASH_UPLOAD_EVERY_MS; flush() sends whatever is left (awaited before
// Finish). Failures are logged and the events retried on the next round.
export class FlashLogUploader {
  private timer: unknown = null;
  private chain: Promise<void> = Promise.resolve();
  private sent = 0;

  constructor(
    private scanId: string,
    private flash: FlashController,
    private api: FlashLogApi,
    private clock: FlashClock = browserClock,
  ) {}

  start(): void {
    if (this.timer !== null) return;
    this.timer = this.clock.every(() => void this.flush(), FLASH_UPLOAD_EVERY_MS);
  }

  // flush uploads pending events (serialized with any upload in flight).
  flush(): Promise<void> {
    this.chain = this.chain.then(async () => {
      const events = this.flash.drainEvents();
      if (!events.length) return;
      try {
        await this.api.postFlash(this.scanId, events);
        this.sent += events.length;
        console.debug(`[openscan/flash] uploaded ${events.length} toggles (${this.sent} total)`);
      } catch (err) {
        this.flash.requeue(events);
        console.warn(`[openscan/flash] flash log upload of ${events.length} toggles failed; retrying later`, err);
      }
    });
    return this.chain;
  }

  // close stops the timer and sends the rest once.
  async close(): Promise<void> {
    if (this.timer !== null) this.clock.cancel(this.timer);
    this.timer = null;
    await this.flush();
  }
}

// parseFlashParam reads ?flash=1 (forces flash lighting capture on).
export function parseFlashParam(search: string): boolean {
  const v = new URLSearchParams(search).get('flash');
  return v === '1' || v === 'true' || v === 'on';
}
